"use client";

import { ChatAssetInline } from "./ChatAssetInline";
import type { ChatStructuredAsset } from "../types";
import React from "react";
import { cn } from "utils";
import { useJsonRenderRuntimeContext } from "../../jsonRender/JsonRenderRuntimeContext";

interface ChatStructuredTableColumn<T> {
  key: string;
  header: string;
  headerClassName?: string;
  cellClassName?: string;
  /** 首列可渲染资产 */
  render: (row: T) => React.ReactNode;
}

interface ChatStructuredDataTableProps<T> {
  columns: ChatStructuredTableColumn<T>[];
  rows: T[];
  /** 从 row 取资产（首列） */
  getAsset?: (row: T) => ChatStructuredAsset;
  /** 首列资产紧凑样式（对比表） */
  assetCompact?: boolean;
  /** 行点击 */
  onRowClick?: (row: T) => void;
  className?: string;
}

/**
 * 简易网格表：顶部分隔表头 + 数据行，贴合 Figma Table / Title 样式。
 * Asset 列：Figma 42577:3864 — w-116px、左对齐、24px 头像 + body_medium_s / body_s 双行文案。
 */
export function ChatStructuredDataTable<T>({
  columns,
  rows,
  getAsset,
  assetCompact,
  onRowClick,
  className,
}: ChatStructuredDataTableProps<T>) {
  const runtimeContext = useJsonRenderRuntimeContext();
  const stickyBgClass =
    runtimeContext?.scene === "chat"
      ? "bg-background_solid_primary"
      : "bg-background_solid_secondary";

  const assetHeaderClass = cn(
    "sticky left-0 z-[2] h-8 w-[116px] shrink-0 items-center px-2 py-2",
    stickyBgClass,
  );

  const assetCellClass = cn(
    "sticky left-0 z-[1] h-12 w-[116px] shrink-0 items-center px-2 py-1.5",
    stickyBgClass,
  );

  const rowClass = cn(
    "flex w-full border-b border-border_white_transparent10 text-left last:border-b-0",
    onRowClick && "cursor-pointer hover:bg-background_white_overlay5",
  );

  const cells = (row: T) =>
    columns.map((col) => (
      <div
        key={col.key}
        className={cn(
          "flex items-center px-2 py-1.5",
          col.key === "asset"
            ? assetCellClass
            : "min-h-12 min-w-[72px] shrink-0 flex-1 justify-center",
          col.cellClassName,
        )}
      >
        {col.key === "asset" && getAsset ? (
          <ChatAssetInline
            asset={getAsset(row)}
            size="md"
            compactSymbol={assetCompact}
            className="max-w-full"
          />
        ) : (
          col.render(row)
        )}
      </div>
    ));

  return (
    <div className={cn("w-full overflow-x-auto no-scrollbar", className)}>
      <div className="flex min-w-full w-max flex-col">
        <div className="flex w-full border-b border-border_white_transparent10">
          {columns.map((col) => (
            <div
              key={col.key}
              className={cn(
                "flex items-center",
                col.key === "asset"
                  ? assetHeaderClass
                  : "min-h-8 min-w-[72px] shrink-0 flex-1 justify-center p-2",
                col.headerClassName,
              )}
            >
              <span
                className={cn(
                  "body_s text-content_quaternary line-clamp-2 break-words leading-4",
                  col.key === "asset" ? "text-left" : "text-center",
                )}
              >
                {col.header}
              </span>
            </div>
          ))}
        </div>
        {rows.map((row, ri) =>
          onRowClick ? (
            <button
              key={ri}
              type="button"
              className={rowClass}
              data-openreplay-label={`ChatStructuredDataTable · Row | ${getAsset?.(row)?.symbol ?? String(ri)}`}
              onClick={() => onRowClick(row)}
            >
              {cells(row)}
            </button>
          ) : (
            <div key={ri} className={rowClass}>
              {cells(row)}
            </div>
          ),
        )}
      </div>
    </div>
  );
}
